import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Globe, User } from "lucide-react";

const LOGO_URL = "/clarity-zones-logo.png";

export default function LandingPage() {
  const { t, lang, toggleLang } = useI18n();
  const { voucherSession } = useAuth();
  const [name, setName] = useState("");
  const navigate = useNavigate();

  const handleStart = () => {
    navigate("/quiz", { state: { name, lang } });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header with logo + lang toggle */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-100">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-3">
          <img
            src={LOGO_URL}
            alt="Clarity Zones"
            className="h-10 w-auto"
            data-testid="header-logo"
          />
          <div className="flex items-center gap-3">
            {voucherSession && (
              <span className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                <User className="w-3 h-3" />
                {voucherSession.alias}
              </span>
            )}
            <button
              data-testid="lang-toggle"
              onClick={toggleLang}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors duration-200 px-3 py-1.5 rounded-sm hover:bg-slate-100"
            >
              <Globe className="w-4 h-4" />
              <span className="font-medium text-sm tracking-wide">{t("lang_toggle")}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section — Swiss Split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left: Content */}
        <div className="flex flex-col justify-center px-8 sm:px-12 lg:px-20 py-32 lg:py-20">
          <div>
            <h1
              className="font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.1] break-words"
              data-testid="hero-headline"
            >
              {t("hero_headline")}
            </h1>

            <p className="mt-8 text-base lg:text-lg text-slate-500 leading-relaxed">
              {t("hero_subtext")}
            </p>

            <div className="mt-8 p-5 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-sm text-slate-600 leading-relaxed">
                {t("hero_instruction")}
              </p>
            </div>

            <div className="mt-10 space-y-3">
              <label className="block text-sm font-medium text-slate-500" htmlFor="name-input">
                {t("name_label")}
              </label>
              <Input
                id="name-input"
                data-testid="name-input"
                type="text"
                placeholder={t("name_placeholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-sm bg-slate-50/50 max-w-sm"
              />
            </div>

            <div className="mt-10">
              <Button
                data-testid="start-button"
                onClick={handleStart}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm px-10 py-3 h-auto font-bold tracking-wide text-base shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                {t("start_button")}
              </Button>
            </div>
          </div>
        </div>

        {/* Right: Image */}
        <div className="hidden lg:block relative overflow-hidden">
          <div className="absolute inset-0 bg-slate-900/5" />
          <img
            src="https://images.pexels.com/photos/1029622/pexels-photo-1029622.jpeg"
            alt="Abstract minimalist architecture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-white/20" />
        </div>
      </div>
    </div>
  );
}
